import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Portal from "components/Portal";

interface DownloadProgressPopupProps {
  imageURL?: string;
  fileType?: string;
}

const DownloadProgressPopup: React.FC<DownloadProgressPopupProps> = ({ imageURL, fileType = "png" }) => {
  const { t } = useTranslation();
  // Local dismiss — lets the user tuck the popup away when it covers the prompt
  // area's Generate button on smaller screens. The upscale keeps running; it just
  // hides the toast. Resets when the popup remounts (next download).
  const [hidden, setHidden] = useState(false);
  const isSvgDownload = fileType === "svg";
  // SVG → "preparing your file"; everything else (4K raster) → "enhancing resolution".
  const title = isSvgDownload ? t("common:download_menu.progress_svg_title") : t("common:download_menu.progress_upscale_title");
  const subtitle = t("common:download_menu.progress_subtitle");

  if (hidden) return null;

  return (
    <Portal>
      {/* z-[100001]: above the z-[99999] detail modals (EditDesign overlay), else this progress popup
          is hidden behind the modal when a 4K download is started from Try-On / Ghost / Mannequin / Studio detail. */}
      <div className="fixed bottom-4 right-4 z-[100001] w-[400px] max-w-[calc(100vw-2rem)] bg-body border border-border rounded-lg shadow-lg flex flex-col gap-3 p-3">
        <div className="flex items-start gap-3">
          {imageURL && <img src={imageURL} alt="" className="w-12 h-12 object-cover rounded flex-shrink-0" />}
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span className="text-foreground text-h6">{title}</span>
            <span className="text-muted-foreground text-bodySm">{subtitle}</span>
          </div>
          <button
            type="button"
            onClick={() => setHidden(true)}
            className="shrink-0 self-start rounded-md px-2 py-1 text-bodySm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {t("common:download_menu.progress_hide")}
          </button>
        </div>

        <div className="h-[4px] w-full bg-muted overflow-hidden rounded-full">
          <div className="h-full bg-purpleGreen animate-design-progress" style={{ "--creating-animation-duration": 200 + "s" } as React.CSSProperties}></div>
        </div>
      </div>
    </Portal>
  );
};

export default DownloadProgressPopup;
